<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {computed, onMounted, reactive, ref, watch} from "vue";
import TextInput from "@/Components/TextInput.vue";
import SimpleComboBox from "@/Components/SimpleComboBox.vue";
import {useErrorStore} from "@/Stores/errorStore";
import {useStatusStore} from "@/Stores/statusStore";
import {usePromptStore} from "@/Stores/promptStore";
import {useCreditStore} from "@/Stores/creditStore";
import DieIcon from "@/Components/Icons/DieIcon.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import TextComboBox from "@/Components/TextComboBox.vue";
import ArtStyleSelector from "@/Components/GenerationForms/ArtStyleSelector.vue";
import SubscriptionPricingModal from "@/Components/Subscriptions/SubscriptionPricingModal.vue";
import {useUserStore} from "@/Stores/userStore";
import PermissionConstants from "@/Constants/permission-constants";

const emits = defineEmits(['close', 'generate-npc']);
const showPricingModal = ref(false);
const errorStore = useErrorStore();
const statusStore = useStatusStore();
const promptStore = usePromptStore();
const creditStore = useCreditStore();

const userStore = useUserStore();

const creditCost = computed(() => creditStore.getCost('npc'));
const canAfford = computed(() => creditStore.canAfford('npc'));

const props = defineProps({
    show: Boolean,
});

const editions = [
    {'id': 1, 'value': "5e", 'label': "D&D 5e"},
    {'id': 2, 'value': "Pf2", 'label': "Pathfinder 2e"},
    {'id': 3, 'value': "ToA", 'label': "Tavern of Azoth"},
];

const visibleEditions = computed(() => {
    if (!userStore.hasPermission(PermissionConstants.permissions.campaignStudioAccess)) {
        return editions.filter(edition => edition.value !== "ToA");
    }
    return editions;
});

const data = reactive({
    request: {
        name: "",
        influence: "",
        equipmentLevel: "",
        proficiency: "",
        style: "Dark Fantasy",
        edition: editions[0].value,
    },
    placeholders: {
        name: "A name for the merchant",
        influence: "Describe the merchant and their equipment.",
        style: "Dark/Gritty",
        equipmentLevel: "Select equipment quality",
        proficiency: "Select a proficiency",
        edition: "Select an edition",
    },
})

const names = [
    "Liora",
    "Caelan",
    "Sylis",
    "Neira",
    "Tarn",
    "Elyon",
    "Riven",
    "Faelan",
    "Zirel",
    "Maelis",
    "Quilo",
    "Venra",
    "Ilis",
    "Cirena",
    "Thalor",
    "Lynx",
    "Serin",
    "Nao",
    "Yrel",
    "Feyrin",
    "Quorin",
    "Zelara",
    "Taelon",
    "Veris",
    "Lyrin",
    "Cyrae",
    "Baelor",
    "Naelia",
    "Erisa",
    "Thyrin"
];

const sellers = [
    "A downtrodden peddler",
    "A noble lizardfolk",
    "A rogue trader camped out in the woods",
    "A thief",
    "A dwarven blacksmith",
    "A merchant",
    "A fisherman",
    "A mystical elven herbalist",
    "A gnome merchant",
    "An orc trader",
    "A halfling baker",
    "A curious kobold",
    "A halfwolf trader",
    "A mad gnome",
    "An elderly bookseller",
    "A humble beekeeper",
    "An aloof stargazer",
    "A troll lumberjack",
    "A cheerful ferryman",
    "A sneaky pickpocket",
    "A shifty alchemist",
    "A stonemason",
    "An eccentric inventor",
    "A mysterious seer",
    "A savvy mermaid trader",
    "A nomadic elf",
    "A jovial dwarf brewer",
    "A quiet monk",
    "A quicksilver smith",
    "A goblin tinkerer",
    "A gnome gardener",
    "A ghostly librarian",
    "An artful siren"
];

const wares = [
    "selling daggers and clubs",
    "selling longbows and crossbows",
    "selling light leathers",
    "selling studded armors",
    "selling chain shirts",
    "selling plate armors",
    "selling protective shields",
    "selling disguise kits",
    "selling thieves' tools",
    "selling bright torches",
    "selling glowing lanterns",
    "selling sturdy bedrolls",
    "selling durable tents and adventuring gear",
    "selling useful chalk and ink",
    "selling gourmet rations",
    "selling herbalism kits",
    "selling simple slings",
    "selling heavy flails",
    "selling basic ropes",
    "selling rapiers",
    "selling maces",
    "selling shortbows",
    "selling throwing darts",
    "selling quarterstaffs",
    "selling battleaxes",
    "selling hand crossbows",
    "selling warhammers",
    "selling spears",
    "selling flails",
    "selling scimitars",
    "selling halberds",
    "selling morningstars",
    "selling slingshots",
    "selling katanas",
    "selling handaxes",
    "selling pikes",
    "selling whips",
    "selling glaives",
    "selling ranged javelins",
    "with concerningly affordable prices",
    "selling daggers and rapiers",
    "selling longbows and shortbows",
    "selling crossbows and hand crossbows",
    "selling light leathers and studded armors",
    "selling chain shirts and plate armors",
    "selling protective shields and battleaxes",
    "selling disguise kits and thieves' tools",
    "selling bright torches and glowing lanterns",
    "selling sturdy bedrolls and durable tents",
    "selling useful chalk and ink",
    "selling gourmet rations and herbalism kits",
    "selling simple slings and slingshots",
    "selling heavy flails and throwing darts",
    "selling maces and clubs",
    "selling quarterstaffs and spears",
    "selling halberds and pikes",
    "selling katanas and scimitars",
    "selling handaxes and battleaxes",
    "selling whips and flails",
    "selling glaives and ranged javelins",
    "selling warhammers and maces",
    "selling shortbows and throwing darts",
    "selling basic ropes and durable tents",
    "selling halberds and glaives",
    "selling katanas and handaxes",
    "selling morningstars and flails",
    "selling plate armors and protective shields",
    "selling hand crossbows and simple slings",
    "selling battleaxes and warhammers",
    "selling quarterstaffs and whips",
];

const EquipmentLevels = [
    {'id': 0, 'value': "None"},
    {'id': 1, 'value': "Garbage"},
    {'id': 2, 'value': "Basic"},
    {'id': 3, 'value': "Advanced"},
    {'id': 4, 'value': "Magic"},
    {'id': 5, 'value': "Rare"},
    {'id': 6, 'value': "Artifact"},
];

const Proficiencies = [
    {'id': 0, 'value': "Civilian"},
    {'id': 1, 'value': "Novice"},
    {'id': 2, 'value': "Journeyman"},
    {'id': 3, 'value': "Expert"},
    {'id': 4, 'value': "Master"},
]

const artStyles = [
    'Dark Fantasy', 'High Fantasy', 'Sketch', 'Ethereal Dream',
    'Realism', 'Pixel Art', 'Graphic Novel', 'Concept Art'
];

function onDieRoll() {
    const getRandomDescription = (array) => array[Math.floor(Math.random()*array.length)];
    const merchantSuggestion = () => `${getRandomDescription(sellers)} ${getRandomDescription(wares)}`;
    data.request.influence = merchantSuggestion()

    data.request.name = names[Math.floor(Math.random() * names.length)];
    data.placeholders.proficiency = Proficiencies[Math.floor(Math.random() * Proficiencies.length)];
    data.request.style = artStyles[Math.floor(Math.random() * artStyles.length)];

    const weightedIndices = [1, 2, 2, 2, 2, 2, 3, 4]; // 2 is weighted 5 times (It's the preferred result)
    const randomIndex = weightedIndices[Math.floor(Math.random() * weightedIndices.length)];
    data.placeholders.equipmentLevel = EquipmentLevels[randomIndex];
}

function generateMerchant() {
    if (!canAfford.value) {
        showPricingModal.value = true;
        return;
    }
    emits('generate-npc', data.request);
    errorStore.clearErrors()
}

onMounted(() => {
    // const getRandomDescription = (array) => array[Math.floor(Math.random()*array.length)];
    // const merchantSuggestion = () => `${getRandomDescription(sellers)} ${getRandomDescription(wares)}`;
    // data.request.influence = merchantSuggestion()
    data.placeholders.proficiency = Proficiencies[2];
    data.placeholders.equipmentLevel = EquipmentLevels[2];

    const keys = ["name", "influence", "style", "edition"];
    keys.forEach(key => {
        if (promptStore.npc[key] != null) {
            data.request[key] = promptStore.npc[key];
        }
    });

    if (promptStore.npc.proficiency != null) {
        const proficiency = Proficiencies.find(proficiency => proficiency.value === promptStore.npc.proficiency);
        data.placeholders.proficiency = proficiency
    }
    if (promptStore.npc.quality != null) {
        const quality = EquipmentLevels.find(proficiency => proficiency.value === promptStore.npc.quality);
        data.placeholders.equipmentLevel = quality
    }
    if (promptStore.npc.edition != null) {
        const edition = editions.find(edition => edition.value.toLowerCase() === promptStore.npc.edition.toLowerCase());
        if (edition) {
            data.request.edition = edition.value;
        }
    }
})

watch(
    () => data.request,
    (updated) => {
        promptStore.npc.influence = updated.influence;
        promptStore.npc.edition = updated.edition;
        promptStore.npc.style = updated.style;
        promptStore.npc.name = updated.name;
        promptStore.npc.quantity = updated.quantity;
        promptStore.npc.proficiency = updated.proficiency;
    },
    { deep: true }
);

</script>

<template>
    <SubscriptionPricingModal
        :show="showPricingModal"
        :highlight-feature="'credits'"
        :title="'Need more credits?'"
        @close="showPricingModal = false"
    />
    <TransitionRoot as="template" :show="show != null">
        <Dialog as="div" class="relative z-10" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="w-full sm:w-auto relative">
                            <div class="absolute right-4 top-2 block sm:hidden">
                                <span class="sr-only">Close</span>
                                <FontAwesomeIcon class="cursor-pointer ml-2" size="lg" :icon="['far', 'xmark']" style="color: #ffffff;" @click.stop="$emit('close')"/>
                            </div>
                            <div class="p-8 bg-gray-700 text-left text-gray-100">
                                <div class="flex justify-between items-center mr-4 mb-4">
                                    <p class="sm:ml-2 text-2xl font-extrabold">Generate Merchant</p>
                                    <div class="flex items-center">
                                        <DieIcon @animation-finished="onDieRoll" aria-label="Randomize"/>
                                    </div>
                                </div>

                                <!-- Art Style Selector - Full Width -->
                                <div class="mb-4">
                                    <ArtStyleSelector
                                        content-type="npc"
                                        v-model="data.request.style"
                                    />
                                </div>

                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div class="sm:col-span-2">
                                        <InputLabel id="influence" for="influence" value="Name (Optional)" class="leading-6"/>
                                        <TextInput id="influence" class="block w-full h-9 text-gray-800 pl-4" v-model="data.request.name" :placeholder="data.placeholders.name"/>
                                    </div>
                                    <div class="sm:col-span-2">
                                        <InputLabel id="description" for="description" value="Description (Be creative)" class="leading-6"/>
                                        <textarea id="description" class="block w-full pl-4 border-0 dark-input" v-model="data.request.influence" :placeholder="data.placeholders.influence"/>
                                    </div>
                                    <div>
                                        <TextComboBox label="Merchant Proficiency" :options="Proficiencies" :default-option="data.placeholders.proficiency" :placeholder="'Merchant Proficiency'" @selected="data.request.proficiency = $event" @updated="data.request.proficiency = $event"/>
                                    </div>
                                    <div>
                                        <TextComboBox label="Equipment Level" :options="EquipmentLevels" :default-option="data.placeholders.equipmentLevel" :placeholder="'Equipment Level'" @selected="data.request.equipmentLevel = $event" @updated="data.request.equipmentLevel = $event"/>
                                    </div>
                                    <div class="sm:col-span-2">
                                        <SimpleComboBox label="Edition" :options="visibleEditions" :default-option="visibleEditions.find(edition => edition.value === data.request.edition)" @selected="data.request.edition = $event"/>
                                    </div>
                                    <div class="sm:col-span-2 flex justify-end mt-2">
                                        <div class="text-right">
                                            <h3 class="w-full text-center">
                                                <span v-if="statusStore.state?.remainingTime > 0"> (Wait {{ statusStore.state?.remainingTime }}s)</span>
                                            </h3>
                                            <PrimaryButton class="!bg-blue-600" @click="generateMerchant" :disabled="statusStore.state?.remainingTime > 0">
                                                Generate
                                                <span v-if="!creditStore.unlimited" class="ml-1 opacity-75">
                                                    ({{ creditCost }})
                                                </span>
                                            </PrimaryButton>
                                            <p v-if="!creditStore.unlimited" class="text-xs text-gray-400 mt-1 text-right">
                                                {{ creditStore.currentBalance }} credits remaining
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<style>
/* Styles removed - chevron toggle no longer needed */
</style>
